import type { Option } from "@tento-lona/sheets";

/**
 * The no-op baseline of one editor cell's writes: the value a commit is
 * compared with to tell a genuine edit from a repeat of what the cell already
 * holds or is already writing.
 *
 * The baseline is the newest value still standing — the cell's own change
 * heard since the last event (`echo`), else the newest write issued after the
 * last observation whose verdict has not come back, else the confirmed value.
 *
 * The confirmed value has one of two sources: an observation (`bind`: the
 * data the cell was bound to, as read or as another writer changed it) or an
 * accepted write (`accepted`). They are ordered as they arrive at the editor,
 * the newer replacing the older, with one exception: this editor's own writes
 * are ordered by issue, so a write accepted after a newer issue was already
 * accepted is stale and changes nothing.
 *
 * Arrival order is the row's order: the SDK mirrors an accepted write into the
 * row as the write resolves, so after an acceptance the row holds the accepted
 * value whatever was observed while it was pending — an observation never
 * makes a pending write ineligible for confirmation. The server keeps the last
 * write it received, which the editor cannot see; when that was the external
 * one, following the row costs at most one redundant write of the external
 * value.
 *
 * An observation does hide the writes issued before it from the baseline
 * while they are pending (the cell now shows the newer observation), and a
 * refusal clears only its own write, by identity, never by equality: a
 * refused value is never confirmed, so two refused writes in either order
 * leave the confirmed value, never a refused predecessor, and committing a
 * refused value again writes again.
 *
 * ```ts
 * const baseline = new CellCommitBaseline<string>();
 * if (value === baseline.value) return; // a no-op
 * const write = baseline.begin(value);
 * cells.write(target, value, actor).then(
 *   () => baseline.accepted(write),
 *   (err: unknown) => { baseline.refused(write); report(err); },
 * );
 * ```
 */
export class CellCommitBaseline<T> {
  /** The newest arrival of an observation or a (not stale) accepted write. */
  #confirmed: Option<T> = null;
  #issued = 0;
  /** The newest issue the server accepted (0: none): an older issue accepted
   *  after it is stale. */
  #acceptedIssue = 0;
  /** The issue count when the cell was last bound: the writes issued up to it
   *  are older than the observation, so they are not the baseline while
   *  pending — but each stays eligible for confirmation when accepted. */
  #observedIssue = 0;
  /** The writes whose verdict has not come back, by issue number. */
  readonly #outstanding = new Map<number, Option<T>>();
  /** The value the cell's own change last showed (`echo`), or `null` for
   *  none since the last issue, settle or bind. */
  #echo: Option<{ readonly value: Option<T> }> = null;

  /** The baseline a commit compares with. */
  get value(): Option<T> {
    if (this.#echo != null) return this.#echo.value;
    const newest = this.#newestOutstanding();
    if (newest > Math.max(this.#observedIssue, this.#acceptedIssue)) {
      return this.#outstanding.get(newest) ?? null;
    }
    return this.#confirmed;
  }

  /** The cell was bound to `value` — what is stored, as read or as another
   *  writer changed it: it is confirmed, and shown over every write issued so
   *  far (which a later acceptance still confirms). */
  bind(value: Option<T>): void {
    this.#confirmed = value;
    this.#observedIssue = this.#issued;
    this.#echo = null;
  }

  /** The cell heard its own write's change (`value`, as the row now shows
   *  it): the baseline until the next issue, settle or bind. */
  echo(value: Option<T>): void {
    this.#echo = { value };
  }

  /** A write of `value` is issued: it is the baseline until its verdict.
   *  Returns the write's identity for `accepted` / `refused`. */
  begin(value: Option<T>): number {
    const issue = ++this.#issued;
    this.#outstanding.set(issue, value);
    this.#echo = null;
    return issue;
  }

  /** The server stored write `issue`: its value is confirmed — over any
   *  observation that arrived while it was pending — unless a newer issue was
   *  accepted first. */
  accepted(issue: number): void {
    const value = this.#outstanding.get(issue);
    if (!this.#outstanding.delete(issue)) return;
    if (issue > this.#acceptedIssue) {
      this.#confirmed = value ?? null;
      this.#acceptedIssue = issue;
    }
    this.#echo = null;
  }

  /** Write `issue` was refused (or never answered): it no longer counts. */
  refused(issue: number): void {
    if (!this.#outstanding.delete(issue)) return;
    this.#echo = null;
  }

  #newestOutstanding(): number {
    let newest = 0;
    for (const issue of this.#outstanding.keys()) newest = Math.max(newest, issue);
    return newest;
  }
}
